import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
	workOrders: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_WORK_ORDERS_REQUEST = "FETCH_WORK_ORDERS_REQUEST";
const FETCH_WORK_ORDERS_SUCCESS = "FETCH_WORK_ORDERS_SUCCESS";
const FETCH_WORK_ORDERS_FAILURE = "FETCH_WORK_ORDERS_FAILURE";
const CREATE_WORK_ORDER_REQUEST = "CREATE_WORK_ORDER_REQUEST";
const CREATE_WORK_ORDER_SUCCESS = "CREATE_WORK_ORDER_SUCCESS";
const CREATE_WORK_ORDER_FAILURE = "CREATE_WORK_ORDER_FAILURE";
const UPDATE_WORK_ORDER_REQUEST = "UPDATE_WORK_ORDER_REQUEST";
const UPDATE_WORK_ORDER_SUCCESS = "UPDATE_WORK_ORDER_SUCCESS";
const UPDATE_WORK_ORDER_FAILURE = "UPDATE_WORK_ORDER_FAILURE";
const DELETE_WORK_ORDER_REQUEST = "DELETE_WORK_ORDER_REQUEST";
const DELETE_WORK_ORDER_SUCCESS = "DELETE_WORK_ORDER_SUCCESS";
const DELETE_WORK_ORDER_FAILURE = "DELETE_WORK_ORDER_FAILURE";

export const getWorkOrdersList =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "asc",
		search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perWorkOrders);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		// Text search fields
		if (search) {
			query.$or = [
				{ vendor: { $like: `%${search}%` } },
				{ id: { $like: `%${search}%` } },
				{ type: { $like: `%${search}%` } },
			];
		}

		// Helper function to get query value from filter
		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		// Multi-select filter fields
		const multiSelectFields = [
			"vehicleId",
			"type",
			"perPersonnelId",
			"fundingSourceId",
			"vendor",
		];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		// Status filter (single select)
		if (filters.status?.value && filters.status.value !== "all") {
			query.status = filters.status.value;
		}

		// Date range filters for openedDate
		if (filters.from) {
			query.openedDate = {
				...(query.openedDate || {}),
				$gte: filters.from,
			};
		}
		if (filters.to) {
			query.openedDate = {
				...(query.openedDate || {}),
				$lte: filters.to,
			};
		}

		// Period filter (preset date ranges)
		if (filters.period?.value) {
			const now = new Date();
			let startDate;

			switch (filters.period.value) {
				case "today":
					startDate = new Date(now.setHours(0, 0, 0, 0));
					break;
				case "week":
					startDate = new Date(now.setDate(now.getDate() - 7));
					break;
				case "month":
					startDate = new Date(now.setMonth(now.getMonth() - 1));
					break;
				case "quarter":
					startDate = new Date(now.setMonth(now.getMonth() - 3));
					break;
				case "year":
					startDate = new Date(
						now.setFullYear(now.getFullYear() - 1),
					);
					break;
				default:
					break;
			}

			if (startDate) {
				query.openedDate = {
					...(query.openedDate || {}),
					$gte: startDate.toISOString().split("T")[0],
				};
			}
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_WORK_ORDERS_REQUEST,
				success: FETCH_WORK_ORDERS_SUCCESS,
				failure: FETCH_WORK_ORDERS_FAILURE,
			},
			apiCall: async () => {
				const response = await service.find({ query });
				return {
					workOrders: response?.data || [],
					total: response.total || 0,
				};
			},
			transformResult: (res) => ({
				workOrders: res?.workOrders,
				total: res?.total,
			}),
			errorMessagePrefix: "Failed to fetch work orders",
		});
	};

export const createWorkOrder =
	(workOrderData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perWorkOrders);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_WORK_ORDER_REQUEST,
				success: CREATE_WORK_ORDER_SUCCESS,
				failure: CREATE_WORK_ORDER_FAILURE,
			},
			apiCall: async () => {
				const workOrder = await service.create({
					...workOrderData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// If vehicle status needs to be updated
				if (workOrderData.newVehicleStatus && workOrderData.vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(workOrderData.vehicleId, {
						status: workOrderData.newVehicleStatus,
					});
				}

				// If odometer needs to be updated
				if (
					workOrderData.odometerAtService &&
					workOrderData.vehicleId
				) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(workOrderData.vehicleId, {
						odometer: workOrderData.odometerAtService,
						lastMileage: workOrderData.odometerAtService,
					});
				}

				return workOrder;
			},
			successMessage: "Work order created successfully",
			errorMessagePrefix: "Failed to create work order",
		});
	};

export const updateWorkOrder =
	(id, workOrderData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perWorkOrders);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_WORK_ORDER_REQUEST,
				success: UPDATE_WORK_ORDER_SUCCESS,
				failure: UPDATE_WORK_ORDER_FAILURE,
			},
			apiCall: async () => {
				const workOrder = await service.patch(id, {
					...workOrderData,
					updatedBy: userId,
				});

				// If vehicle status needs to be updated
				if (workOrderData.newVehicleStatus && workOrderData.vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(workOrderData.vehicleId, {
						status: workOrderData.newVehicleStatus,
					});
				}

				// If odometer needs to be updated
				if (
					workOrderData.odometerAtService &&
					workOrderData.vehicleId
				) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(workOrderData.vehicleId, {
						odometer: workOrderData.odometerAtService,
						lastMileage: workOrderData.odometerAtService,
					});
				}

				return workOrder;
			},
			successMessage: "Work order updated successfully",
			errorMessagePrefix: "Failed to update work order",
		});
	};

export const closeWorkOrder =
	(id, closedDate, vehicleId, newVehicleStatus, odometerAtService, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perWorkOrders);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_WORK_ORDER_REQUEST,
				success: UPDATE_WORK_ORDER_SUCCESS,
				failure: UPDATE_WORK_ORDER_FAILURE,
			},
			apiCall: async () => {
				const workOrder = await service.patch(id, {
					status: "Closed",
					closedDate: closedDate || new Date().toISOString(),
					odometerAtService: odometerAtService || null,
					updatedBy: userId,
				});

				// Update vehicle status if provided
				if (newVehicleStatus && vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(vehicleId, {
						status: newVehicleStatus,
					});
				}

				// Update odometer if provided
				if (odometerAtService && vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(vehicleId, {
						odometer: odometerAtService,
						lastMileage: odometerAtService,
					});
				}

				return workOrder;
			},
			successMessage: "Work order closed successfully",
			errorMessagePrefix: "Failed to close work order",
		});
	};

export const reopenWorkOrder = (id, userId) => async (dispatch) => {
	const service = getService(serviceNames.perWorkOrders);
	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: UPDATE_WORK_ORDER_REQUEST,
			success: UPDATE_WORK_ORDER_SUCCESS,
			failure: UPDATE_WORK_ORDER_FAILURE,
		},
		apiCall: async () => {
			const workOrder = await service.patch(id, {
				status: "Open",
				closedDate: null,
				updatedBy: userId,
			});
			return workOrder;
		},
		successMessage: "Work order reopened successfully",
		errorMessagePrefix: "Failed to reopen work order",
	});
};

export const deleteWorkOrder = (id, userId) => async (dispatch) => {
	const service = getService(serviceNames.perWorkOrders);
	const patchData = {
		isDeleted: true,
		deletedBy: userId,
		deletedDateTime: new Date().toISOString(),
	};

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: DELETE_WORK_ORDER_REQUEST,
			success: DELETE_WORK_ORDER_SUCCESS,
			failure: DELETE_WORK_ORDER_FAILURE,
		},
		apiCall: async () => {
			await service.patch(id, patchData);
			return id;
		},
		transformResult: () => id,
		successMessage: "Work order deleted successfully",
		errorMessagePrefix: "Failed to delete work order",
	});
};

export default function workOrdersReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_WORK_ORDERS_REQUEST:
		case CREATE_WORK_ORDER_REQUEST:
		case UPDATE_WORK_ORDER_REQUEST:
		case DELETE_WORK_ORDER_REQUEST:
			return { ...state, loading: true, error: null };
		case FETCH_WORK_ORDERS_SUCCESS:
			return {
				...state,
				loading: false,
				workOrders: action.payload.workOrders,
				total: action.payload.total,
			};
		case CREATE_WORK_ORDER_SUCCESS:
			return {
				...state,
				loading: false,
				workOrders: [...state.workOrders, action.payload],
				total: state.total + 1,
			};
		case UPDATE_WORK_ORDER_SUCCESS:
			return {
				...state,
				loading: false,
				workOrders: state.workOrders.map((wo) =>
					wo.id === action.payload.id ? action.payload : wo,
				),
			};
		case DELETE_WORK_ORDER_SUCCESS:
			return {
				...state,
				loading: false,
				workOrders: state.workOrders.filter(
					(wo) => wo.id !== action.payload,
				),
				total: state.total - 1,
			};
		case FETCH_WORK_ORDERS_FAILURE:
		case CREATE_WORK_ORDER_FAILURE:
		case UPDATE_WORK_ORDER_FAILURE:
		case DELETE_WORK_ORDER_FAILURE:
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
}
